import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useTickets = (user, token, showMessage) => {
  const [tickets, setTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);

  // Forms and selected state
  const [ticketForm, setTicketForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [ticketToClose, setTicketToClose] = useState(null);
  const [closeComment, setCloseComment] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [ticketForNote, setTicketForNote] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Filters
  const [ticketFilters, setTicketFilters] = useState({
    sort_by: 'created_at',
    sort_order: 'desc',
    status_filter: '',
    priority_filter: '',
    assigned_to_filter: '',
    created_by_filter: '',
    search: '',
  });

  // Fetch active tickets
  const fetchTickets = useCallback(async () => {
    if (!token) return;
    try {
      const queryParams = new URLSearchParams();
      if (ticketFilters.sort_by) queryParams.append('sort_by', ticketFilters.sort_by);
      if (ticketFilters.sort_order) queryParams.append('sort_order', ticketFilters.sort_order);
      if (ticketFilters.status_filter) queryParams.append('status_filter', ticketFilters.status_filter);
      if (ticketFilters.priority_filter) queryParams.append('priority_filter', ticketFilters.priority_filter);
      if (ticketFilters.assigned_to_filter) queryParams.append('assigned_to_filter', ticketFilters.assigned_to_filter);
      if (ticketFilters.created_by_filter) queryParams.append('created_by_filter', ticketFilters.created_by_filter);
      if (ticketFilters.search) queryParams.append('search', ticketFilters.search);

      const response = await apiClient(`/api/tickets/?${queryParams.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Fetch tickets error:', error);
    }
  }, [token, ticketFilters]);

  // Fetch closed tickets
  const fetchClosedTickets = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/tickets/closed');
      if (response.ok) {
        const data = await response.json();
        setClosedTickets(data);
      }
    } catch (error) {
      console.error('Fetch closed tickets error:', error);
    }
  }, [token]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/dashboard/stats');
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Fetch dashboard stats error:', error);
    }
  }, [token]);

  // Fetch assignable users
  const fetchAssignableUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/users/assignable');
      if (response.ok) {
        const data = await response.json();
        setAssignableUsers(data);
      }
    } catch (error) {
      console.error('Fetch assignable users error:', error);
    }
  }, [token]);

  // Create ticket
  const handleCreateTicket = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingTickets(true);

    try {
      const formData = new FormData();
      formData.append('title', ticketForm.title);
      formData.append('description', ticketForm.description);
      formData.append('priority', ticketForm.priority);
      if (ticketForm.assigned_to) {
        formData.append('assigned_to', ticketForm.assigned_to);
      }
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await apiClient('/api/tickets/', {
        method: 'POST',
        isFormData: true,
        body: formData,
      });

      if (response.ok) {
        await fetchTickets();
        await fetchDashboardStats();
        setTicketForm({ title: '', description: '', priority: 'medium', assigned_to: '' });
        setSelectedFile(null);
        if (showMessage) showMessage('Ticket created successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to create ticket', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error creating ticket', 'error');
      return false;
    } finally {
      setIsLoadingTickets(false);
    }
  };

  // Update ticket (status, priority, assigned_to)
  const handleUpdateTicket = async (ticketId, updateData) => {
    setIsLoadingTickets(true);
    try {
      const response = await apiClient(`/api/tickets/${ticketId}`, {
        method: 'PUT',
        body: updateData,
      });

      if (response.ok) {
        await fetchTickets();
        await fetchDashboardStats();
        setSelectedTicket(null);
        if (showMessage) showMessage('Ticket updated successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update ticket', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating ticket', 'error');
    } finally {
      setIsLoadingTickets(false);
    }
  };

  // Close ticket with comment
  const handleCloseTicket = async () => {
    if (!ticketToClose || !closeComment.trim()) {
      if (showMessage) showMessage('Please provide a comment before closing the ticket.', 'error');
      return;
    }

    setIsLoadingTickets(true);
    try {
      const response = await apiClient(`/api/tickets/${ticketToClose.id}/close`, {
        method: 'POST',
        body: { close_comment: closeComment },
      });

      if (response.ok) {
        await fetchTickets();
        await fetchClosedTickets();
        await fetchDashboardStats();
        setShowCloseModal(false);
        setTicketToClose(null);
        setCloseComment('');
        if (showMessage) showMessage('Ticket closed successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to close ticket', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error closing ticket', 'error');
    } finally {
      setIsLoadingTickets(false);
    }
  };

  // Add note to ticket
  const handleAddTicketNote = async (ticketId, noteContent) => {
    if (!ticketId || !noteContent?.trim()) return false;
    setIsLoadingTickets(true);
    try {
      const response = await apiClient(`/api/tickets/${ticketId}/notes`, {
        method: 'POST',
        body: { content: noteContent.trim() }
      });
      if (response.ok) {
        const updatedTicket = await response.json();
        await fetchTickets();
        if (selectedTicketDetail && selectedTicketDetail.id === ticketId) {
          setSelectedTicketDetail(updatedTicket);
        }
        if (showMessage) showMessage('Note added to ticket successfully!', 'success');
        return updatedTicket;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to add note', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error adding note', 'error');
      return false;
    } finally {
      setIsLoadingTickets(false);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicketDetail(ticket);
    setShowTicketDetailModal(true);
  };

  return {
    tickets,
    closedTickets,
    dashboardStats,
    assignableUsers,
    isLoadingTickets,
    ticketForm,
    setTicketForm,
    selectedTicket,
    setSelectedTicket,
    selectedTicketDetail,
    setSelectedTicketDetail,
    showTicketDetailModal,
    setShowTicketDetailModal,
    showCloseModal,
    setShowCloseModal,
    ticketToClose,
    setTicketToClose,
    closeComment,
    setCloseComment,
    showNoteModal,
    setShowNoteModal,
    ticketForNote,
    setTicketForNote,
    selectedFile,
    setSelectedFile,
    ticketFilters,
    setTicketFilters,
    fetchTickets,
    fetchClosedTickets,
    fetchDashboardStats,
    fetchAssignableUsers,
    handleCreateTicket,
    handleUpdateTicket,
    handleCloseTicket,
    handleAddTicketNote,
    handleTicketClick,
  };
};
